import {
  GetLanguagesOptions,
  GetLanguagesResult,
  getLanguages,
  GetLanguagesFields,
} from '../fetchers/languages';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { assignMswData, useInternalState, serializeQuery } from '../hooks/internalState';
import { useCallback } from 'react';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetLanguagesOptions<Fields extends GetLanguagesFields> = GetLanguagesOptions<Fields>;

/**
 * Get all languages
 * This method returns all available video languages.
 */

export function useGetLanguages<Fields extends GetLanguagesFields>(
  getOptions: UseGetLanguagesOptions<Fields> | (() => UseGetLanguagesOptions<Fields> | null),
  config?: SWRConfiguration<GetLanguagesResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/languages${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getLanguages({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetLanguages, {
    endpoint: '/languages',
    method: 'GET',
  });
}

/**
 * Get all languages
 * This method returns all available video languages.
 */

export function useGetLanguagesLazy<Fields extends GetLanguagesFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetLanguagesResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetLanguagesOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/languages${serializeQuery(options)}`,
          getLanguages({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetLanguagesLazy, {
    endpoint: '/languages',
    method: 'GET',
  });
}

type UseGetLanguagesOptionsInfinite<Fields extends GetLanguagesFields> =
  GetLanguagesOptions<Fields>;

/**
 * Get all languages
 * This method returns all available video languages.
 */

export function useGetLanguagesInfinite<Fields extends GetLanguagesFields>(
  getOptions:
    | UseGetLanguagesOptionsInfinite<Fields>
    | (() => UseGetLanguagesOptionsInfinite<Fields> | null),
  config?: SWRInfiniteConfiguration<GetLanguagesResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let getKey = (pageIndex: number, previousPageData: GetLanguagesResult<Fields> | null) => {
    if (options === null) {
      return null;
    }
    if (previousPageData && !previousPageData.paging.next) {
      return null;
    }
    let { perPage = 25, page: _page, ...rest } = options.query ?? {};
    let select = options.select.join(',');
    let queryString = Object.entries(rest ?? {})
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return [
      `/languages?page=${pageIndex + 1}&perPage=${perPage}&fields=${select}&${queryString}`,
      pageIndex,
    ];
  };
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWRInfinite(
    getKey,
    options !== null
      ? ([_uri, pageIndex]: [string, number]) => {
          return getLanguages({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            query: { ...options.query, page: pageIndex + 1 },
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetLanguagesInfinite, {
    endpoint: '/languages',
    method: 'GET',
  });
}
